import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Paper,
    Popover,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { KeyboardDatePicker } from '@material-ui/pickers'
import DomainIcon from '@material-ui/icons/Domain'
import EventIcon from '@material-ui/icons/Event'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'

import { useUserTags } from '../../contexts/UserTagsContext'
import { currencyFormatter } from '../../helpers'
import {
    DamageStatus,
    DamageStatusCounts,
    getDamageStatusName,
} from '../../models'
import { UserTag } from '../../models/Users/types'

const BRAND_TEAL = '#008C85'

/** Dialog paper height cap (viewport-relative, no fixed px max). */
const DIALOG_MAX_HEIGHT = '92vh'

/**
 * Scroll region for table / search-empty / loading placeholder.
 * Uses viewport minus approximate dialog chrome; stable when search filters rows.
 */
const BREAKDOWN_TABLE_VIEWPORT =
    'max(240px, min(calc(92vh - 340px), calc(100vh - 360px)))'

export interface PropertyDamageSummaryRow {
    apartment_id: number
    name: string
    damage_count: number
    total_cost: number
}

function tagsForApartment(apartmentId: number, userTags: UserTag[]): UserTag[] {
    return userTags.filter((tag) =>
        (tag.apartments ?? []).includes(apartmentId),
    )
}

function formatFilterDate(date: Date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

type PropertyBreakdownSortKey = 'name' | 'damage_count' | 'total_cost'

type PropertyBreakdownSortState = {
    key: PropertyBreakdownSortKey
    dir: 'asc' | 'desc'
}

export interface PropertyDamageBreakdownModalProps {
    open: boolean
    loading: boolean
    rows: PropertyDamageSummaryRow[]
    createdAfter: Date
    createdBefore: Date
    selectedStatuses: DamageStatus[]
    statusCounts: DamageStatusCounts | null
    onDateChange: (startDate: Date, endDate: Date) => void
    onStatusChange: (statuses: DamageStatus[]) => void
    onClose: () => void
}

const filterTriggerSx = (theme: {
    palette: {
        background: { paper: string }
        text: { secondary: string; primary: string }
    }
}): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${fade(BRAND_TEAL, 0.35)}`,
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
})

const BreakdownDateFilter: React.FC<{
    startDate: Date
    endDate: Date
    onDateChange: (startDate: Date, endDate: Date) => void
}> = ({ startDate, endDate, onDateChange }) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate)
    const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate)

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setTempStartDate(startDate)
        setTempEndDate(endDate)
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        if (
            tempStartDate &&
            tempEndDate &&
            (tempStartDate.getTime() !== startDate.getTime() ||
                tempEndDate.getTime() !== endDate.getTime())
        ) {
            onDateChange(tempStartDate, tempEndDate)
        }
        setAnchorEl(null)
    }

    return (
        <>
            <ButtonBase
                focusRipple
                onClick={handleOpen}
                style={filterTriggerSx(theme)}
                aria-label="Filter by created date range"
            >
                <EventIcon style={{ color: BRAND_TEAL, fontSize: 18 }} />
                <Box minWidth={0}>
                    <Typography
                        variant="caption"
                        display="block"
                        style={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            fontSize: 10,
                        }}
                    >
                        Created
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: theme.palette.text.primary,
                        }}
                    >
                        {formatFilterDate(startDate)} –{' '}
                        {formatFilterDate(endDate)}
                    </Typography>
                </Box>
                <ExpandMoreIcon
                    style={{
                        color: theme.palette.text.secondary,
                        fontSize: 18,
                        marginLeft: 4,
                    }}
                />
            </ButtonBase>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    style: {
                        borderRadius: 10,
                        marginTop: 6,
                        boxShadow: theme.shadows[6],
                        border: `1px solid ${theme.palette.divider}`,
                    },
                }}
            >
                <Box
                    p={2}
                    display="flex"
                    flexDirection="column"
                    style={{ gap: theme.spacing(2), minWidth: 260 }}
                >
                    <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 700, color: BRAND_TEAL }}
                    >
                        Date range
                    </Typography>
                    <KeyboardDatePicker
                        label="Created after"
                        value={tempStartDate}
                        onChange={(date) => {
                            if (!date) return
                            setTempStartDate(date)
                            if (tempEndDate && date > tempEndDate) {
                                setTempEndDate(date)
                            }
                        }}
                        format="MM/dd/yyyy"
                        maxDate={tempEndDate || undefined}
                        fullWidth
                        inputVariant="outlined"
                    />
                    <KeyboardDatePicker
                        label="Created before"
                        value={tempEndDate}
                        onChange={(date) => {
                            if (!date) return
                            setTempEndDate(date)
                            if (tempStartDate && date < tempStartDate) {
                                setTempStartDate(date)
                            }
                        }}
                        format="MM/dd/yyyy"
                        minDate={tempStartDate || undefined}
                        fullWidth
                        inputVariant="outlined"
                    />
                </Box>
            </Popover>
        </>
    )
}

const BreakdownStatusFilter: React.FC<{
    selectedStatuses: DamageStatus[]
    statusCounts: DamageStatusCounts | null
    onStatusChange: (statuses: DamageStatus[]) => void
}> = ({ selectedStatuses, statusCounts, onStatusChange }) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const summary =
        selectedStatuses.length === 0
            ? 'All statuses'
            : selectedStatuses.length === 1
            ? getDamageStatusName(selectedStatuses[0])
            : `${selectedStatuses.length} statuses`

    const handleToggle = (status: DamageStatus) => {
        const next = selectedStatuses.includes(status)
            ? selectedStatuses.filter((s) => s !== status)
            : [...selectedStatuses, status]
        onStatusChange(next)
    }

    return (
        <>
            <ButtonBase
                focusRipple
                onClick={(e) => setAnchorEl(e.currentTarget)}
                style={filterTriggerSx(theme)}
                aria-label="Filter by damage status"
            >
                <Box
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: BRAND_TEAL,
                        flexShrink: 0,
                    }}
                />
                <Box minWidth={0}>
                    <Typography
                        variant="caption"
                        display="block"
                        style={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            fontSize: 10,
                        }}
                    >
                        Status
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: theme.palette.text.primary,
                        }}
                    >
                        {summary}
                    </Typography>
                </Box>
                <ExpandMoreIcon
                    style={{
                        color: theme.palette.text.secondary,
                        fontSize: 18,
                        marginLeft: 4,
                    }}
                />
            </ButtonBase>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    style: {
                        borderRadius: 10,
                        marginTop: 6,
                        minWidth: 260,
                        maxHeight: 360,
                        boxShadow: theme.shadows[6],
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                    },
                }}
            >
                <Box
                    px={2}
                    pt={2}
                    pb={1}
                    style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        background: `linear-gradient(135deg, ${fade(
                            BRAND_TEAL,
                            0.08,
                        )} 0%, transparent 100%)`,
                    }}
                >
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Filter by status
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Portfolio counts for the current date range
                    </Typography>
                </Box>
                <List
                    dense
                    style={{ padding: theme.spacing(1, 0), overflowY: 'auto' }}
                >
                    {Object.values(DamageStatus).map((status) => {
                        const checked = selectedStatuses.includes(status)
                        const count = statusCounts?.[status] ?? 0
                        return (
                            <ListItem
                                key={status}
                                style={{
                                    paddingTop: 4,
                                    paddingBottom: 4,
                                    paddingLeft: theme.spacing(2),
                                    paddingRight: theme.spacing(2),
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            onChange={() =>
                                                handleToggle(status)
                                            }
                                            color="primary"
                                            size="small"
                                            style={{
                                                color: checked
                                                    ? BRAND_TEAL
                                                    : undefined,
                                            }}
                                        />
                                    }
                                    label={
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            style={{
                                                gap: theme.spacing(2),
                                                minWidth: 180,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {getDamageStatusName(status)}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={count}
                                                style={{
                                                    height: 22,
                                                    fontWeight: 700,
                                                    fontVariantNumeric:
                                                        'tabular-nums',
                                                    backgroundColor: fade(
                                                        BRAND_TEAL,
                                                        0.12,
                                                    ),
                                                    color: BRAND_TEAL,
                                                }}
                                            />
                                        </Box>
                                    }
                                    style={{ margin: 0, width: '100%' }}
                                />
                            </ListItem>
                        )
                    })}
                </List>
            </Popover>
        </>
    )
}

const defaultSort: PropertyBreakdownSortState = { key: 'name', dir: 'asc' }

export const PropertyDamageBreakdownModal = ({
    open,
    loading,
    rows,
    createdAfter,
    createdBefore,
    selectedStatuses,
    statusCounts,
    onDateChange,
    onStatusChange,
    onClose,
}: PropertyDamageBreakdownModalProps) => {
    const theme = useTheme()
    const { userTags } = useUserTags()
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<PropertyBreakdownSortState>(defaultSort)
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

    useEffect(() => {
        if (!open) {
            setSearch('')
            setSort(defaultSort)
            setSelectedTagId(null)
        }
    }, [open])

    const workingRows = useMemo(() => {
        if (selectedTagId == null) return rows
        const tag = userTags.find((t) => t.id === selectedTagId)
        const ids = tag?.apartments ?? []
        if (ids.length === 0) return []
        const allowed = new Set(ids)
        return rows.filter((r) => allowed.has(r.apartment_id))
    }, [rows, selectedTagId, userTags])

    const totals = useMemo(() => {
        return workingRows.reduce(
            (acc, r) => ({
                damageCount: acc.damageCount + r.damage_count,
                totalCost: acc.totalCost + r.total_cost,
            }),
            { damageCount: 0, totalCost: 0 },
        )
    }, [workingRows])

    const displayedRows = useMemo(() => {
        const q = search.trim().toLowerCase()
        const filtered = q
            ? workingRows.filter((r) =>
                  (r.name || '').toLowerCase().includes(q),
              )
            : workingRows
        const { key, dir } = sort
        const mult = dir === 'asc' ? 1 : -1
        return [...filtered].sort((a, b) => {
            if (key === 'name') {
                return (
                    mult *
                    (a.name || '').localeCompare(b.name || '', undefined, {
                        sensitivity: 'base',
                    })
                )
            }
            if (key === 'damage_count') {
                return mult * (a.damage_count - b.damage_count)
            }
            return mult * (a.total_cost - b.total_cost)
        })
    }, [workingRows, search, sort])

    const handleTagChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            const value = event.target.value
            setSelectedTagId(value === 'all' ? null : (value as number))
        },
        [],
    )

    const handleSort = useCallback((column: PropertyBreakdownSortKey) => {
        setSort((prev) =>
            prev.key === column
                ? {
                      key: column,
                      dir: prev.dir === 'asc' ? 'desc' : 'asc',
                  }
                : {
                      key: column,
                      dir: column === 'name' ? 'asc' : 'desc',
                  },
        )
    }, [])

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                style: {
                    borderRadius: 12,
                    maxHeight: DIALOG_MAX_HEIGHT,
                    ...(loading && {
                        minHeight: 'min(82vh, calc(100vh - 48px))',
                    }),
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle
                disableTypography
                style={{
                    padding: theme.spacing(2.5, 3),
                    background: `linear-gradient(135deg, ${fade(
                        BRAND_TEAL,
                        0.12,
                    )} 0%, ${fade(BRAND_TEAL, 0.04)} 100%)`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                    <Box
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            backgroundColor: fade(BRAND_TEAL, 0.18),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <DomainIcon
                            style={{ color: BRAND_TEAL, fontSize: 26 }}
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.25,
                            }}
                        >
                            Damage by property
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 4, lineHeight: 1.45 }}
                        >
                            Date and status filters match the damage list
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                style={{
                    padding: theme.spacing(2, 3, 0),
                    flex: '1 1 auto',
                    minHeight: loading
                        ? 'max(240px, min(48vh, calc(92vh - 420px)))'
                        : 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="stretch"
                    style={{
                        gap: theme.spacing(1.25),
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(1.25),
                        borderRadius: 10,
                        backgroundColor: fade(BRAND_TEAL, 0.05),
                        border: `1px solid ${fade(BRAND_TEAL, 0.14)}`,
                    }}
                >
                    <BreakdownDateFilter
                        startDate={createdAfter}
                        endDate={createdBefore}
                        onDateChange={onDateChange}
                    />
                    <BreakdownStatusFilter
                        selectedStatuses={selectedStatuses}
                        statusCounts={statusCounts}
                        onStatusChange={onStatusChange}
                    />
                </Box>

                {loading ? (
                    <Box
                        flex={1}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            height: BREAKDOWN_TABLE_VIEWPORT,
                            minHeight: BREAKDOWN_TABLE_VIEWPORT,
                            maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                            borderRadius: 8,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.grey[50],
                            gap: theme.spacing(2),
                        }}
                    >
                        <CircularProgress
                            size={36}
                            thickness={3}
                            style={{ color: BRAND_TEAL }}
                        />
                        <Typography variant="body1" color="textSecondary">
                            Loading breakdown…
                        </Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    <Box
                        py={5}
                        px={2}
                        textAlign="center"
                        borderRadius={8}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            border: `1px dashed ${theme.palette.divider}`,
                        }}
                    >
                        <Typography color="textSecondary" variant="body1">
                            No matching damages for any property with these
                            filters.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="flex-start"
                            style={{
                                gap: theme.spacing(2),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search by property name…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                    fontSize: 20,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                style={{ flex: '1 1 200px', minWidth: 200 }}
                            />
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{
                                    minWidth: 180,
                                    flex: '0 0 auto',
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <InputLabel id="breakdown-property-tag-label">
                                    Property tag
                                </InputLabel>
                                <Select
                                    labelId="breakdown-property-tag-label"
                                    id="breakdown-property-tag-select"
                                    value={selectedTagId ?? 'all'}
                                    onChange={handleTagChange}
                                    label="Property tag"
                                >
                                    <MenuItem value="all">
                                        All properties
                                    </MenuItem>
                                    {userTags.map((tag) => (
                                        <MenuItem key={tag.id} value={tag.id}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Box
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            tag.color,
                                                        marginRight: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                {tag.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {rows.length > 0 && workingRows.length === 0 ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                py={5}
                                px={2}
                                textAlign="center"
                                borderRadius={8}
                                style={{
                                    flex: '1 1 auto',
                                    minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                    maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                    backgroundColor: theme.palette.grey[50],
                                    border: `1px dashed ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    color="textSecondary"
                                    variant="body1"
                                >
                                    No properties in the selected property tag
                                    match this breakdown.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    style={{
                                        gap: theme.spacing(1),
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={`${workingRows.length} propert${
                                            workingRows.length === 1
                                                ? 'y'
                                                : 'ies'
                                        }`}
                                        style={{
                                            fontWeight: 600,
                                            backgroundColor: fade(
                                                BRAND_TEAL,
                                                0.14,
                                            ),
                                            border: `1px solid ${fade(
                                                BRAND_TEAL,
                                                0.35,
                                            )}`,
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${totals.damageCount.toLocaleString()} damages`}
                                        style={{ fontWeight: 600 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={currencyFormatter.format(
                                            totals.totalCost,
                                        )}
                                        style={{
                                            fontWeight: 700,
                                            color: BRAND_TEAL,
                                            backgroundColor: fade(
                                                BRAND_TEAL,
                                                0.08,
                                            ),
                                        }}
                                    />
                                </Box>
                                {search.trim() !== '' &&
                                    displayedRows.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            style={{
                                                marginTop: -theme.spacing(1),
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            Showing {displayedRows.length} of{' '}
                                            {workingRows.length} propert
                                            {workingRows.length === 1
                                                ? 'y'
                                                : 'ies'}
                                        </Typography>
                                    )}

                                {displayedRows.length === 0 ? (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        py={4}
                                        px={2}
                                        textAlign="center"
                                        borderRadius={8}
                                        style={{
                                            flex: '1 1 auto',
                                            minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            backgroundColor:
                                                theme.palette.grey[50],
                                            border: `1px dashed ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            variant="body1"
                                        >
                                            No properties match your search.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        style={{
                                            flex: '1 1 auto',
                                            height: BREAKDOWN_TABLE_VIEWPORT,
                                            minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            overflow: 'auto',
                                            borderRadius: 8,
                                            boxShadow: 'none',
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '52%',
                                                        }}
                                                    >
                                                        <TableSortLabel
                                                            active={
                                                                sort.key ===
                                                                'name'
                                                            }
                                                            direction={
                                                                sort.key ===
                                                                'name'
                                                                    ? sort.dir
                                                                    : 'asc'
                                                            }
                                                            onClick={() =>
                                                                handleSort(
                                                                    'name',
                                                                )
                                                            }
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 11,
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.06em',
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .secondary,
                                                            }}
                                                        >
                                                            Property
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '20%',
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="flex-end"
                                                            width="100%"
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    sort.key ===
                                                                    'damage_count'
                                                                }
                                                                direction={
                                                                    sort.key ===
                                                                    'damage_count'
                                                                        ? sort.dir
                                                                        : 'desc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort(
                                                                        'damage_count',
                                                                    )
                                                                }
                                                                style={{
                                                                    fontWeight: 700,
                                                                    fontSize: 11,
                                                                    textTransform:
                                                                        'uppercase',
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                Damages
                                                            </TableSortLabel>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '28%',
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="flex-end"
                                                            width="100%"
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    sort.key ===
                                                                    'total_cost'
                                                                }
                                                                direction={
                                                                    sort.key ===
                                                                    'total_cost'
                                                                        ? sort.dir
                                                                        : 'desc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort(
                                                                        'total_cost',
                                                                    )
                                                                }
                                                                style={{
                                                                    fontWeight: 700,
                                                                    fontSize: 11,
                                                                    textTransform:
                                                                        'uppercase',
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                Total cost
                                                            </TableSortLabel>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayedRows.map(
                                                    (row, index) => {
                                                        const rowTags = tagsForApartment(
                                                            row.apartment_id,
                                                            userTags,
                                                        )
                                                        return (
                                                            <TableRow
                                                                key={
                                                                    row.apartment_id
                                                                }
                                                                hover
                                                                style={{
                                                                    backgroundColor:
                                                                        index %
                                                                            2 ===
                                                                        0
                                                                            ? undefined
                                                                            : fade(
                                                                                  theme
                                                                                      .palette
                                                                                      .common
                                                                                      .black,
                                                                                  0.02,
                                                                              ),
                                                                }}
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                        verticalAlign:
                                                                            'middle',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        flexWrap="wrap"
                                                                        alignItems="center"
                                                                        style={{
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            component="span"
                                                                            variant="body2"
                                                                            style={{
                                                                                fontWeight: 500,
                                                                                lineHeight: 1.4,
                                                                            }}
                                                                        >
                                                                            {row.name ||
                                                                                '—'}
                                                                        </Typography>
                                                                        {rowTags.map(
                                                                            (
                                                                                tag,
                                                                            ) => (
                                                                                <Chip
                                                                                    key={
                                                                                        tag.id
                                                                                    }
                                                                                    size="small"
                                                                                    label={
                                                                                        tag.name
                                                                                    }
                                                                                    style={{
                                                                                        height: 22,
                                                                                        fontSize: 11,
                                                                                        fontWeight: 600,
                                                                                        backgroundColor: fade(
                                                                                            tag.color,
                                                                                            0.18,
                                                                                        ),
                                                                                        border: `1px solid ${fade(
                                                                                            tag.color,
                                                                                            0.45,
                                                                                        )}`,
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{
                                                                        fontFamily:
                                                                            'Roboto Mono, monospace',
                                                                        fontSize: 13,
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                    }}
                                                                >
                                                                    {row.damage_count.toLocaleString()}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        color: BRAND_TEAL,
                                                                        fontVariantNumeric:
                                                                            'tabular-nums',
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                    }}
                                                                >
                                                                    {currencyFormatter.format(
                                                                        row.total_cost,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    },
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {!loading && workingRows.length > 0 && (
                <Box
                    px={3}
                    py={2}
                    style={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ gap: theme.spacing(2) }}
                    >
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            Portfolio total
                        </Typography>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="baseline"
                            style={{ gap: theme.spacing(3) }}
                        >
                            <Typography variant="body2">
                                <strong>
                                    {totals.damageCount.toLocaleString()}
                                </strong>{' '}
                                damages
                            </Typography>
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 700,
                                    color: BRAND_TEAL,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {currencyFormatter.format(totals.totalCost)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            <DialogActions
                style={{
                    padding: theme.spacing(1.5, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: '#fff',
                }}
            >
                <Button
                    onClick={() => onClose()}
                    variant="contained"
                    color="primary"
                    disableElevation
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        paddingLeft: theme.spacing(3),
                        paddingRight: theme.spacing(3),
                        borderRadius: 8,
                        backgroundColor: BRAND_TEAL,
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
